import { useCallback, useEffect, useState } from 'react';
import { useProjects, type ProjectDto } from '../api/hooks.js';

/**
 * The project the operator is looking at, shared by every screen.
 *
 * It used to be `useState('')` inside each page, defaulting to the first
 * project the API happened to return. So choosing a project on one screen and
 * navigating to another silently put you on somebody else's project — and
 * coming back to the first screen lost the choice again. For a product whose
 * whole job is "look at what happened on this site", quietly changing which
 * site you are looking at is worse than asking.
 *
 * One value, in localStorage, with a listener per mounted screen so two
 * screens on the page never disagree. `storage` only fires in *other* tabs,
 * which is why the change is also broadcast in this one.
 */
const KEY = 'ro.activeProject';
const EVENT = 'ro:active-project';

function read(): string {
  try {
    return localStorage.getItem(KEY) ?? '';
  } catch {
    // Private windows and blocked site data: a remembered project is a
    // convenience, never a requirement.
    return '';
  }
}

function write(id: string): void {
  try {
    localStorage.setItem(KEY, id);
  } catch {
    /* see read() */
  }
  window.dispatchEvent(new CustomEvent(EVENT, { detail: id }));
}

export interface ActiveProject {
  /**
   * What the operator picked, verbatim. `''` means "all projects" — a real
   * answer on the screens that can show more than one at a time.
   */
  selected: string;
  /**
   * The same choice resolved to one project, for screens that cannot mean
   * "all" — a heatmap or an ingest key belongs to exactly one project. Falls
   * back to the first in the list, the same first for every screen.
   */
  projectId: string;
  setProjectId: (id: string) => void;
  projects: ProjectDto[];
  /** True until the project list has arrived. */
  loading: boolean;
}

/**
 * Turns the remembered choice into the two answers screens ask for.
 *
 * A remembered id that no longer exists — a deleted project, or one belonging
 * to another organization — must not leave every screen querying a 404, so it
 * is treated as no choice at all.
 *
 * `projectId` is resolved rather than written back. Storing the fallback would
 * turn an explicit "all projects" on one screen into a single project on every
 * other one; resolving it keeps the screens agreeing without overwriting what
 * the operator actually picked.
 */
export function resolveProject(
  stored: string,
  projects: { id: string }[],
): { selected: string; projectId: string } {
  const selected = projects.some((p) => p.id === stored) ? stored : '';
  return { selected, projectId: selected || (projects[0]?.id ?? '') };
}

export function useActiveProject(orgId?: string): ActiveProject {
  const { data: projects, isLoading } = useProjects(orgId);
  const [stored, setStored] = useState(read);

  useEffect(() => {
    const onLocal = (e: Event) => setStored((e as CustomEvent<string>).detail);
    const onOtherTab = (e: StorageEvent) => {
      if (e.key === KEY) setStored(e.newValue ?? '');
    };
    window.addEventListener(EVENT, onLocal);
    window.addEventListener('storage', onOtherTab);
    return () => {
      window.removeEventListener(EVENT, onLocal);
      window.removeEventListener('storage', onOtherTab);
    };
  }, []);

  const { selected, projectId } = resolveProject(stored, projects ?? []);
  const list = projects ?? [];

  const setProjectId = useCallback((id: string) => {
    setStored(id);
    write(id);
  }, []);

  return { selected, projectId, setProjectId, projects: list, loading: isLoading };
}
